import { Grid, Stack, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { APP_IMAGES } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import Label from "../../../components/Label";
import UploadBox from "../../../components/UploadBox";
import {
    getAdminSetting,
    updateAdminSetting,
} from "../../../services/adminSettingApiFn";
import { baseUrl } from "../../../services";
import { handleOpenSnackBar } from "../../../utils";
import { useTranslation } from "react-i18next";

const initialValues = {
    image_1: "",
    image_2: "",
    image_3: "",
    image_4: "",
};

const CheckoutPromotionalImages = () => {
    const { t } = useTranslation(["setting"]);
    const openImageNormalEnRef = useRef(null);
    const openImageNormalRoRef = useRef(null);
    const openImagePlanEnRef = useRef(null);
    const openImagePlanRoRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState({
        image_1: false,
        image_2: false,
        image_3: false,
        image_4: false,
    });
    const [setting, setSetting] = useState(null);
    const [inputValues, setInputValues] = useState(initialValues);

    const fetchAdminSetting = async () => {
        try {
            setIsLoading(true);
            const response = await getAdminSetting();
            if (response.data.status === "success") {
                const data = response.data.data;
                setSetting(data);
                setInputValues({
                    image_1: data?.promotionalImages?.normalCheckout?.en || "",
                    image_2: data?.promotionalImages?.normalCheckout?.ro || "",
                    image_3: data?.promotionalImages?.checkoutPlan?.en || "",
                    image_4: data?.promotionalImages?.checkoutPlan?.ro || "",
                });
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAdminSetting();
    }, []);

    const handleUploadImage = async (event, key) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploadLoading((prev) => ({ ...prev, [key]: true }));
            const formData = new FormData();
            formData.append("image", file);

            const response = await axios({
                method: "post",
                url: `${baseUrl}image/upload`,
                data: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });

            setInputValues((prev) => ({
                ...prev,
                [key]: response?.data?.url || "",
            }));
            handleOpenSnackBar("success", t("success.mediaUploadSuccess"));
        } catch (error) {
            handleOpenSnackBar("error", t("error.mediaUploadFail"));
        } finally {
            if (openImageNormalEnRef.current) openImageNormalEnRef.current.value = null;
            if (openImageNormalRoRef.current) openImageNormalRoRef.current.value = null;
            if (openImagePlanEnRef.current) openImagePlanEnRef.current.value = null;
            if (openImagePlanRoRef.current) openImagePlanRoRef.current.value = null;
            setUploadLoading((prev) => ({ ...prev, [key]: false }));
        }
    };

    const updateSetting = async () => {
        try {
            const isAnyImageMissing = [
                inputValues.image_1,
                inputValues.image_2,
                inputValues.image_3,
                inputValues.image_4,
            ].some((image) => !image);

            if (isAnyImageMissing) {
                return handleOpenSnackBar(
                    "error",
                    t("validation.uploadAllImagesBeforeUpdating"),
                );
            }

            const payload = {
                promotionalImages: {
                    normalCheckout: {
                        en: inputValues.image_1,
                        ro: inputValues.image_2,
                    },
                    checkoutPlan: {
                        en: inputValues.image_3,
                        ro: inputValues.image_4,
                    },
                },
            };

            setIsLoading(true);
            const response = await updateAdminSetting(setting?._id, payload);
            if (response.data.status === "success") {
                handleOpenSnackBar("success", response.data.message);
                fetchAdminSetting();
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error?.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <AppLoader isVisible={isLoading} />
            <PaperContainer
                title={t("checkoutPromotionalImages")}
                subtitle={t("uploadImagesDescription")}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6" fontWeight={600} textTransform={"capitalize"}>
                            {t("normalCheckout")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <UploadBox
                            inputRef={openImageNormalEnRef}
                            label={t("english")}
                            value={inputValues.image_1}
                            loading={uploadLoading.image_1}
                            placeholderImage={APP_IMAGES.uploadImage}
                            accept="image/png, image/jpeg, image/jpg, image/webp"
                            onClick={() => openImageNormalEnRef.current.click()}
                            onChange={(event) => handleUploadImage(event, "image_1")}
                            onRemove={() => setInputValues((prev) => ({ ...prev, image_1: "" }))}
                            previewSizeProps={{ width: "100%", height: "auto" }}
                            imageProps={{
                                width: "100%",
                                height: "100%",
                                sx: { objectFit: "cover" },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <UploadBox
                            inputRef={openImageNormalRoRef}
                            label={t("romanian")}
                            value={inputValues.image_2}
                            loading={uploadLoading.image_2}
                            placeholderImage={APP_IMAGES.uploadImage}
                            accept="image/png, image/jpeg, image/jpg, image/webp"
                            onClick={() => openImageNormalRoRef.current.click()}
                            onChange={(event) => handleUploadImage(event, "image_2")}
                            onRemove={() => setInputValues((prev) => ({ ...prev, image_2: "" }))}
                            previewSizeProps={{ width: "100%", height: "auto" }}
                            imageProps={{
                                width: "100%",
                                height: "100%",
                                sx: { objectFit: "cover" },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" fontWeight={600} textTransform={"capitalize"}>
                            {t("checkoutPlan")}
                        </Typography>
                    </Grid> 
                    <Grid item xs={12} md={6}>
                        <UploadBox
                            inputRef={openImagePlanEnRef}
                            label={t("english")}
                            value={inputValues.image_3}
                            loading={uploadLoading.image_3}
                            placeholderImage={APP_IMAGES.uploadImage}
                            accept="image/png, image/jpeg, image/jpg, image/webp"
                            onClick={() => openImagePlanEnRef.current.click()}
                            onChange={(event) => handleUploadImage(event, "image_3")}
                            onRemove={() => setInputValues((prev) => ({ ...prev, image_3: "" }))}
                            previewSizeProps={{ width: "100%", height: "auto" }}
                            imageProps={{
                                width: "100%",
                                height: "100%",
                                sx: { objectFit: "cover" },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <UploadBox
                            inputRef={openImagePlanRoRef}
                            label={t("romanian")}
                            value={inputValues.image_4}
                            loading={uploadLoading.image_4}
                            placeholderImage={APP_IMAGES.uploadImage}
                            accept="image/png, image/jpeg, image/jpg, image/webp"
                            onClick={() => openImagePlanRoRef.current.click()}
                            onChange={(event) => handleUploadImage(event, "image_4")}
                            onRemove={() => setInputValues((prev) => ({ ...prev, image_4: "" }))}
                            previewSizeProps={{ width: "100%", height: "auto" }}
                            imageProps={{
                                width: "100%",
                                height: "100%",
                                sx: { objectFit: "cover" },
                            }}
                        />
                    </Grid>
                </Grid>

                <Stack alignItems={"flex-end"} mt={3}>
                    <GlobalButton
                        variant={"contained"}
                        btnText={t("update")}
                        handleClickBtn={updateSetting}
                        styleProps={{ width: 150, height: 40 }}
                    />
                </Stack>
            </PaperContainer>
        </>
    );
};

export default CheckoutPromotionalImages;
